import React from "react";

import { computeRebalance } from "../../utils/rebalance";

/**
 * TotalInfoDescription — Hero + Bento 카드.
 *
 * `variant` (DAS-175 전략 3분할):
 * - 미지정(기존 호출부·테스트): 전체 잔액 Hero + 리밸런싱 6카드 — 종전 동작 그대로.
 * - "overview": 전체 뷰. Hero만 렌더하고 서브라인을 전략 3분해로 바꾼다.
 *               (리밸런싱 지표는 델타전략만의 숫자라 전체 화면에서 뺀다 — Option A의 핵심)
 * - "delta":    델타전략 뷰. Hero 값이 보유 BTC를 뺀 운용액이 되고, 카드도 리밸런싱 5장만 남는다.
 */
class TotalInfoDescription extends React.Component {
    formatNumberWithCommaAndDecimal = (number, decimalPlaces = 3) => {
        if (isNaN(number)) return "";
        return Number(number).toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        });
    };

    render() {
        const {
            totalBalance,
            totalFiatBalance,
            totalRiskyBalance,
            btcSpotBalance,
            prevTotalBalance,
            stockSummary,
            onStockCardClick,
            variant,
        } = this.props;

        const isOverview = variant === "overview";
        const isDelta = variant === "delta";

        const hasPrev =
            prevTotalBalance != null &&
            Number.isFinite(prevTotalBalance) &&
            prevTotalBalance > 0;
        const deltaPercent = hasPrev
            ? ((totalBalance - prevTotalBalance) / prevTotalBalance) * 100
            : null;
        // 직전 데이터 대비 변화 금액(KRW). 상승/하락을 보여줄 때 "얼마에서 얼마로"를
        // 함께 보여 맥락을 준다 (DAS-145).
        // 변화율/변화액은 기존과 동일하게 코인 잔고(WS 갱신) 기준으로만 계산한다 —
        // 미국주식은 5분 폴링이라 실시간 델타 산정 대상에 포함하지 않는다 (DAS-164).
        const deltaAmount = hasPrev ? totalBalance - prevTotalBalance : null;

        // 리밸런싱 계산은 보유 BTC 현물 평가액을 제외한 잔액을 기준으로 한다 (DAS-172).
        // 보유 BTC는 이동 대상이 아니므로 총액에 포함하면 목표 Risky 잔액과 보내야 할
        // 금액이 부풀려진다. 잔액 표시 카드(전체/Fiat/Risky)는 실보유 그대로 둔다.
        const {
            heldBtcValue,
            base: rebalanceBase,
            deltaFiatBalance,
            requiredRisky: requiredFutures,
            sendBalance,
            receiveExchange,
        } = computeRebalance({ totalBalance, totalFiatBalance, totalRiskyBalance, btcSpotBalance });

        // 미국주식 요약 — 등록된 적립이 없으면(API 실패 포함) total_value_krw가 0/미정.
        // 이 경우 Hero 합산·서브라인은 생략하고 카드는 진입점 안내만 노출한다 (DAS-164).
        const stockValue = Number(stockSummary?.total_value_krw);
        const hasStock = Number.isFinite(stockValue) && stockValue > 0;
        const combinedTotal = hasStock ? totalBalance + stockValue : totalBalance;
        const stockPrincipal = Number(stockSummary?.principal_krw) || 0;
        const stockPnlPct = Number(stockSummary?.pnl_pct);
        const hasStockPnlPct = Number.isFinite(stockPnlPct);

        const rebalanceCards = [
            {
                label: isDelta ? "Fiat 잔액 (BTC 제외)" : "Fiat 잔액 (KRW)",
                value: `₩${this.formatNumberWithCommaAndDecimal(isDelta ? deltaFiatBalance : totalFiatBalance, 0)}`,
            },
            {
                label: "Risky 잔액 (KRW)",
                value: `₩${this.formatNumberWithCommaAndDecimal(totalRiskyBalance, 0)}`,
            },
            {
                label: "필요 Risky 잔액",
                value: `₩${this.formatNumberWithCommaAndDecimal(requiredFutures, 0)}`,
            },
            {
                label: "보내야 할 금액",
                value: `₩${this.formatNumberWithCommaAndDecimal(sendBalance, 0)}`,
                tooltip: "보유 BTC를 제외한 잔액의 25%가 목표 Risky 잔액입니다. 현재 Risky 잔액과의 차이만큼 이동이 필요합니다.",
            },
            {
                label: "받는 곳",
                value: receiveExchange,
                tooltip: "Fiat이 목표 Risky 잔액보다 많으면 Risky로, Risky가 목표보다 많으면 Fiat으로 이동합니다.",
            },
        ];

        const stockEntryCard = {
                // 미국주식 적립 진입점 카드 — 항상 노출하며 클릭 시 대시보드 미국주식 탭으로 전환 (DAS-164 /stocks 통합 흡수)
                label: "미국주식",
                value: hasStock ? `₩${this.formatNumberWithCommaAndDecimal(stockValue, 0)}` : "—",
                onClick: onStockCardClick,
                sub: hasStock
                    ? [
                          {
                              // pnl_pct 미수신(NaN) 시 부호·손익색 없이 중립 표기 (리뷰 MINOR 2)
                              text: `${hasStockPnlPct && stockPnlPct >= 0 ? "+" : ""}${hasStockPnlPct ? stockPnlPct.toFixed(2) : "0.00"}%`,
                              className: hasStockPnlPct
                                  ? stockPnlPct >= 0
                                      ? "text-gain"
                                      : "text-loss"
                                  : "text-content-muted",
                          },
                          {
                              text: `원금 ₩${this.formatNumberWithCommaAndDecimal(stockPrincipal, 0)}`,
                              className: "text-content-muted",
                          },
                      ]
                    : [{ text: "등록된 적립 없음", className: "text-content-muted" }],
        };

        // Bento 카드 구성 — overview는 카드 없이 Hero만, delta는 리밸런싱 5장만, 기존 호출부는 종전 6장.
        const gridCards = isOverview ? [] : isDelta ? rebalanceCards : [...rebalanceCards, stockEntryCard];

        const heroLabel = isDelta ? "델타 운용액" : "전체 잔액";
        const heroValue = `₩${this.formatNumberWithCommaAndDecimal(isDelta ? rebalanceBase : combinedTotal, 0)}`;
        // 델타 뷰의 Hero 값은 BTC를 뺀 운용액인데 변화율은 코인 총액 기준이라 두 숫자가 맞물리지 않는다 —
        // 산술 모순을 만들지 않도록 델타 뷰에서는 변화 표기를 숨긴다.
        const showDelta = !isDelta && deltaPercent !== null && Number.isFinite(deltaPercent);

        // Fiat 비율 (0~100%) — 델타 뷰는 BTC 제외 기준으로 맞춘다
        const ratioNumerator = isDelta ? deltaFiatBalance : totalFiatBalance;
        const ratioDenominator = isDelta ? rebalanceBase : totalBalance;
        const fiatRatio =
            ratioDenominator > 0
                ? Math.min(100, Math.max(0, (ratioNumerator / ratioDenominator) * 100))
                : 0;

        return (
            <div className="space-y-3 sm:space-y-4">
                {/* Hero 카드: 앰비언트 그라디언트 메쉬 */}
                <div
                    className="relative overflow-hidden rounded-2xl border border-surface-border p-4 shadow-elevated sm:p-6"
                    style={{
                        background:
                            "radial-gradient(ellipse 600px 400px at 20% 50%, var(--color-brand-subtle) 0%, transparent 70%), " +
                            "radial-gradient(ellipse 400px 300px at 80% 30%, var(--color-gain-subtle) 0%, transparent 70%), " +
                            "linear-gradient(180deg, var(--color-surface-card) 0%, var(--color-surface) 100%)",
                    }}
                >
                    {/* 노이즈 텍스처 오버레이 */}
                    <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none rounded-2xl" />
                    <div className="relative z-10">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
                                {heroLabel}
                            </span>
                            {isDelta && (
                                <span className="rounded-full bg-brand-subtle px-2 py-0.5 text-xs font-medium text-content-secondary">
                                    보유 BTC 제외
                                </span>
                            )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-num text-2xl font-bold text-content sm:text-3xl">
                                {heroValue}
                            </span>
                            {showDelta && (
                                <span className={`inline-flex items-center gap-1 font-num text-xs font-semibold ${deltaPercent >= 0 ? "text-gain" : "text-loss"}`}>
                                    {/* 델타는 WS 실시간인 코인 잔고 기준 — 합산 표시 중엔 범위를 명시해 산술 모순 방지 (리뷰 MAJOR 1) */}
                                    {hasStock && <span className="font-sans font-medium opacity-70">코인</span>}
                                    {deltaPercent >= 0 ? (
                                        <svg width="8" height="6" viewBox="0 0 8 6" fill="currentColor" aria-hidden="true"><path d="M4 0L8 6H0L4 0Z"/></svg>
                                    ) : (
                                        <svg width="8" height="6" viewBox="0 0 8 6" fill="currentColor" aria-hidden="true"><path d="M4 6L0 0H8L4 6Z"/></svg>
                                    )}
                                    {deltaAmount >= 0 ? "+" : "-"}₩{this.formatNumberWithCommaAndDecimal(Math.abs(deltaAmount), 0)}
                                    <span className="opacity-70">({Math.abs(deltaPercent).toFixed(2)}%)</span>
                                </span>
                            )}
                        </div>
                        {hasPrev && !isDelta && (
                            <div className="mt-1 font-num text-xs text-content-muted">
                                직전{hasStock ? "(코인)" : ""} ₩{this.formatNumberWithCommaAndDecimal(prevTotalBalance, 0)}
                            </div>
                        )}
                        {/* 델타 뷰: 같은 업비트 계좌에서 무엇을 뺐는지 밝힌다 (DAS-175) */}
                        {isDelta && (
                            <div className="mt-1 font-num text-xs text-content-muted">
                                코인 잔고 ₩{this.formatNumberWithCommaAndDecimal(totalBalance, 0)} 중 BTC 적립분 ₩
                                {this.formatNumberWithCommaAndDecimal(heldBtcValue, 0)} 제외
                            </div>
                        )}
                        {/* 전체 뷰: 전략 3분해. 그 외에는 코인/미국주식 2분해 (DAS-164) */}
                        {isOverview ? (
                            <div className="mt-1 font-num text-xs text-content-muted">
                                BTC 적립 ₩{this.formatNumberWithCommaAndDecimal(heldBtcValue, 0)} · 델타전략 ₩
                                {this.formatNumberWithCommaAndDecimal(rebalanceBase, 0)}
                                {hasStock && ` · 해외주식 적립 ₩${this.formatNumberWithCommaAndDecimal(stockValue, 0)}`}
                            </div>
                        ) : (
                            hasStock &&
                            !isDelta && (
                                <div className="mt-1 font-num text-xs text-content-muted">
                                    코인 ₩{this.formatNumberWithCommaAndDecimal(totalBalance, 0)} · 미국주식 ₩{this.formatNumberWithCommaAndDecimal(stockValue, 0)}
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Bento Grid: 비대칭 레이아웃 — Fiat 카드 row-span-2 (overview는 카드 없이 Hero만) */}
                {gridCards.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:gap-3 sm:grid-cols-3">
                    {gridCards.map((card, index) => {
                        const CardTag = card.onClick ? "button" : "div";
                        const cardButtonProps = card.onClick ? { type: "button", onClick: card.onClick } : {};
                        return (
                        <CardTag
                            key={card.label}
                            {...cardButtonProps}
                            className={`min-w-0 overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-card ${
                                index === 0
                                    ? "sm:row-span-2 flex flex-col justify-between p-3 sm:p-4"
                                    : "p-3 sm:p-4"
                            } ${card.onClick ? "block w-full text-left transition-colors hover:bg-surface-hover" : ""}`}
                        >
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
                                    {card.tooltip ? (
                                        <span className="flex items-center gap-1">
                                            {card.label}
                                            <span
                                                title={card.tooltip}
                                                className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-content-muted text-xs text-content-muted leading-none"
                                            >?</span>
                                        </span>
                                    ) : card.label}
                                </div>
                                <div
                                    className={`mt-1 truncate font-num font-semibold text-content sm:mt-2 ${
                                        index === 0
                                            ? "text-base sm:text-2xl"
                                            : "text-sm sm:text-lg"
                                    }`}
                                >
                                    {card.value}
                                </div>
                                {/* 미국주식 카드: 수익률 · 원금(또는 미등록 안내) 서브라인 */}
                                {card.sub && (
                                    <div className="mt-1 flex flex-wrap items-center gap-x-1.5 font-num text-xs">
                                        {card.sub.map((line) => (
                                            <span key={line.text} className={line.className}>{line.text}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Fiat 카드: Fiat vs Risky 스택형 비율 바 */}
                            {index === 0 && (
                                <div
                                    className="mt-4"
                                    role="img"
                                    aria-label={`Fiat ${fiatRatio.toFixed(1)}%, Risky ${(100 - fiatRatio).toFixed(1)}% of total`}
                                >
                                    <div className="flex h-2.5 overflow-hidden rounded-full bg-surface-elevated">
                                        <div
                                            className="bg-brand transition-[width] duration-700"
                                            style={{ width: `${fiatRatio}%` }}
                                        />
                                        <div
                                            className="bg-warning-subtle transition-[width] duration-700"
                                            style={{ width: `${100 - fiatRatio}%` }}
                                        />
                                    </div>
                                    <div className="mt-1 flex items-center justify-between font-num text-xs text-content-muted">
                                        <span>Fiat {fiatRatio.toFixed(0)}%</span>
                                        <span>Risky {(100 - fiatRatio).toFixed(0)}%</span>
                                    </div>
                                </div>
                            )}
                        </CardTag>
                        );
                    })}
                </div>
                )}
            </div>
        );
    }
}

export default TotalInfoDescription;
